/* PBW: one-click add buttons for the "You may also like" strip in the side cart.
   Uses the same cart events as the theme's own add to cart, so the drawer refreshes by itself.
   Safety net: if the drawer still shows the product we just added a moment later (for example when
   another line in the cart is sitting at its stock limit), the drawer is refreshed here directly. */
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

const release = (button) => {
  if (!button) return;
  button.disabled = false;
  button.classList.remove('is-loading');
};

if (!window.__pbwUpsellReady) {
  window.__pbwUpsellReady = true;

  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-pbw-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');

    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    const sectionId = sectionIds[0] || 'cart-drawer-section';
    formData.append('sections', sectionIds.length ? sectionIds.join(',') : sectionId);

    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const json = await response.json();
      if (json.status) throw new Error(json.message || 'Could not add to cart');

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: AbortSignal.timeout(8000),
      });
      const ajaxCart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'pbw-upsell',
          sourceId: 'pbw-upsell',
          itemCount: 1,
          productId,
          sections: json.sections,
          didError: false,
        },
      });

      // Safety net: the added product should have left the strip by now.
      setTimeout(async () => {
        const stale = document.querySelector(`[data-pbw-upsell-add][data-variant-id="${variantId}"]`);
        if (!stale) return;
        try {
          let markup = json.sections && json.sections[sectionId];
          if (!markup) {
            const refreshed = await fetch(`${window.location.pathname}?section_id=${sectionId}`, { credentials: 'same-origin' });
            markup = await refreshed.text();
          }
          await morphSection(sectionId, markup, { mode: 'full' });
        } catch (error) {
          console.warn('[pbw-upsell] refresh failed', error);
        } finally {
          document
            .querySelectorAll('[data-pbw-upsell-add].is-loading, [data-pbw-upsell-add]:disabled')
            .forEach((el) => release(el));
        }
      }, 1500);
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      release(button);
    }
  });
}
